import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { getCustomerTagsOfTeam } from "@redotech/redo-merchant-app-common/client/customer-tags";
import {
  CreateCustomerTagModal,
  getBadgePropsForCustomerTag,
} from "@redotech/redo-merchant-app-common/customer-tags";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  CustomerTagOperation,
  ManageCustomerTagsStep,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { isCustomerTagImmutableByRedo } from "@redotech/redo-model/customer";
import { CustomerTagRecord } from "@redotech/redo-model/customer-tags/customer-tag-record";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import {
  LabeledInput,
  LabelOrientation,
  LabelPosition,
  LabelSize,
} from "@redotech/redo-web/labeled-input";
import { memo, useMemo, useState } from "react";
import { FrontendStepType, PartialWithRequiredType } from "../steps";
import { useDelayedAutoFocus } from "./common/use-delayed-auto-focus";

const CustomerTagManager = memo(function CustomerTagManager({
  step,
  setStep,
  showValidationErrors,
}: {
  step: PartialWithRequiredType<ManageCustomerTagsStep>;
  setStep: (step: PartialWithRequiredType<ManageCustomerTagsStep>) => void;
  showValidationErrors?: boolean;
}) {
  const client = useRequiredContext(RedoMerchantClientContext);
  const team = useRequiredContext(TeamContext);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [tagCount, setTagCount] = useState(0);
  const [tagsDropdownTouched, setTagsDropdownTouched] = useState(
    showValidationErrors || false,
  );

  const customerTagsLoad = useLoad(
    async (signal) =>
      getCustomerTagsOfTeam(client, { teamId: team._id }, signal),
    [team._id, tagCount],
  );

  const tagOptions: BadgeRedoListItem<CustomerTagRecord>[] = useMemo(() => {
    return (
      customerTagsLoad.value
        ?.filter((tag) => !isCustomerTagImmutableByRedo(tag))
        .map((tag) => ({
          id: tag._id,
          type: "badge" as const,
          value: tag,
          badge: getBadgePropsForCustomerTag(tag),
        })) || []
    );
  }, [customerTagsLoad.value]);

  // Operation selection options
  const operationOptions = useMemo(
    () => [
      {
        id: "add",
        text: "Add tags",
        type: "text" as const,
        value: CustomerTagOperation.ADD,
      },
      {
        id: "remove",
        text: "Remove tags",
        type: "text" as const,
        value: CustomerTagOperation.REMOVE,
      },
    ],
    [],
  );

  const selectedOperation = useMemo(() => {
    return operationOptions.find((op) => op.value === step.operation);
  }, [operationOptions, step.operation]);

  const selectedTagOptions = useMemo(() => {
    if (!step.tagIds || step.tagIds.length === 0) {
      return [];
    }
    return tagOptions.filter((tag) => step.tagIds?.includes(tag.value._id));
  }, [step.tagIds, tagOptions]);

  const handleOperationSelect = useHandler((option: any) => {
    setStep({
      ...step,
      operation: option.value,
      // Clear tag selection when changing operation
      tagIds: [],
    });
  });

  const handleTagsSelect = useHandler((selectedItems: any[]) => {
    setStep({ ...step, tagIds: selectedItems.map((item) => item.value._id) });
  });

  const handleCreateTag = useHandler(async (createdTag: CustomerTagRecord) => {
    // Increment tag count to trigger reload
    setTagCount((prev) => prev + 1);

    // Add the newly created tag to the current selection
    setStep({ ...step, tagIds: [...(step.tagIds || []), createdTag._id] });
  });

  const hasNoTags = !step.tagIds || step.tagIds.length === 0;

  const errorText = useMemo(() => {
    if (step.operation && hasNoTags && tagsDropdownTouched) {
      return "At least one tag is required";
    }
    return undefined;
  }, [hasNoTags, step.operation, tagsDropdownTouched]);

  const delayedAutoFocus = useDelayedAutoFocus(hasNoTags);

  return (
    <Flex dir="column" gap="md">
      <LabeledInput
        label="Operation"
        orientation={LabelOrientation.VERTICAL}
        position={LabelPosition.ABOVE}
        size={LabelSize.EXTRA_SMALL}
      >
        <RedoSingleSelectDropdownInput
          options={operationOptions}
          optionSelected={({ item }: { item: any }) =>
            handleOperationSelect(item)
          }
          placeholder="Select operation..."
          selectedItem={selectedOperation}
        />
      </LabeledInput>

      <LabeledInput
        label="Customer tags"
        orientation={LabelOrientation.VERTICAL}
        position={LabelPosition.ABOVE}
        size={LabelSize.EXTRA_SMALL}
      >
        <Flex dir="column" gap="sm" w="full">
          <RedoButton
            hierarchy="secondary"
            IconLeading={PlusIcon}
            onClick={() => setShowCreateModal(true)}
            size="sm"
            text="New tag"
          />
          <RedoTagsDropdownInput
            autoFocus={delayedAutoFocus}
            description={errorText}
            key={delayedAutoFocus.toString()}
            loading={customerTagsLoad.pending}
            onBlur={() => setTagsDropdownTouched(true)}
            options={tagOptions}
            placeholder="Select customer tags..."
            selectedOptions={selectedTagOptions}
            setSelectedOptions={handleTagsSelect}
            size="xs"
            state={errorText ? "error" : "default"}
          />
        </Flex>
      </LabeledInput>
      <CreateCustomerTagModal
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateTag}
        open={showCreateModal}
      />
    </Flex>
  );
});

export const validateManageCustomerTagsStep = (
  step: PartialWithRequiredType<ManageCustomerTagsStep>,
) => {
  return !!(step.operation && step.tagIds && step.tagIds.length > 0);
};

export const manageCustomerTagsStepType: FrontendStepType<
  PartialWithRequiredType<ManageCustomerTagsStep>
> = {
  valid: validateManageCustomerTagsStep,
  type: "standard",
  inspectorFactory: (props) => ({
    header: { title: "Manage customer tags" },
    sections: [
      {
        fields: [
          { widget: "toggle", label: "Deactivate step", path: "disabled" },
          {
            path: "customerTags",
            label: "",
            widget: "custom",
            Element: ({ value: step, valueChange: setStep }) => (
              <CustomerTagManager
                setStep={setStep}
                showValidationErrors={props.showValidationErrors}
                step={step}
              />
            ),
          },
        ],
      },
    ],
  }),
};
